import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiMail, FiLock, FiArrowRight, FiSmartphone, FiShield } from 'react-icons/fi';
import logo from '../assets/logo.png';
import icon from '../assets/icon.png';

const Login: React.FC = () => {
    const [mode, setMode] = useState<'password' | 'otp-request' | 'otp-verify'>('password');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [userId, setUserId] = useState<number>(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const { login, loginOtp, verifyOtp } = useAuth();
    const navigate = useNavigate();

    const handlePasswordLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            await login(email, password);
            navigate('/admin');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erreur de connexion');
        } finally { setLoading(false); }
    };

    const handleOtpRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            const data = await loginOtp(email);
            setUserId(data.userId);
            setMessage(`Code OTP envoye au ${data.telephone || 'telephone'}`);
            setMode('otp-verify');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Erreur');
        } finally { setLoading(false); }
    };

    const handleOtpVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            await verifyOtp(userId, otp);
            navigate('/admin');
        } catch (err: any) {
            setError(err.response?.data?.error || 'OTP invalide');
        } finally { setLoading(false); }
    };

    return (
        <div className="login-page">
            {/* Left panel - branding */}
            <div className="login-brand-panel">
                <div className="login-brand-content">
                    <div className="login-brand-icon-wrap">
                        <img src={icon} alt="" className="login-brand-icon" />
                    </div>
                    <img src={logo} alt="SalesPulse" className="login-brand-logo" />
                    <p className="login-brand-tagline">
                        Pilotez votre force de vente avec precision et efficacite
                    </p>
                    <div className="login-brand-features">
                        <div className="login-brand-feature">
                            <span className="login-brand-feature-dot" />
                            Suivi temps reel des commerciaux
                        </div>
                        <div className="login-brand-feature">
                            <span className="login-brand-feature-dot" />
                            Gestion des objectifs et bonus
                        </div>
                        <div className="login-brand-feature">
                            <span className="login-brand-feature-dot" />
                            Rapports et analyses detailles
                        </div>
                    </div>
                </div>
                <div className="login-brand-footer">
                    SalesPulse v2.0
                </div>
            </div>

            {/* Right panel - form */}
            <div className="login-form-panel">
                <div className="login-card">
                    <div className="login-card-header">
                        <img src={icon} alt="" className="login-card-icon-mobile" />
                        <h1>Bienvenue</h1>
                        <p>Connectez-vous a votre espace</p>
                    </div>

                    {error && (
                        <div className="login-alert login-alert-error">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                            {error}
                        </div>
                    )}
                    {message && (
                        <div className="login-alert login-alert-success">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M5.5 8.5l2 2 3.5-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            {message}
                        </div>
                    )}

                    {/* Mode tabs */}
                    <div className="login-tabs">
                        <button
                            className={`login-tab ${mode === 'password' ? 'active' : ''}`}
                            onClick={() => { setMode('password'); setError(''); setMessage(''); }}
                        >
                            <FiLock size={14} /> Mot de passe
                        </button>
                        <button
                            className={`login-tab ${mode !== 'password' ? 'active' : ''}`}
                            onClick={() => { setMode('otp-request'); setError(''); setMessage(''); }}
                        >
                            <FiSmartphone size={14} /> OTP
                        </button>
                    </div>

                    {mode === 'password' && (
                        <form onSubmit={handlePasswordLogin} className="login-form-inner">
                            <div className="login-field">
                                <label>Adresse email</label>
                                <div className="login-input-wrap">
                                    <FiMail className="login-input-icon" />
                                    <input
                                        type="email" value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="votre@email.com" required autoFocus
                                    />
                                </div>
                            </div>
                            <div className="login-field">
                                <label>Mot de passe</label>
                                <div className="login-input-wrap">
                                    <FiLock className="login-input-icon" />
                                    <input
                                        type="password" value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder="Votre mot de passe" required
                                    />
                                </div>
                            </div>
                            <button type="submit" className="login-submit" disabled={loading}>
                                {loading ? (
                                    <span className="login-spinner" />
                                ) : (
                                    <>Se connecter <FiArrowRight size={18} /></>
                                )}
                            </button>
                        </form>
                    )}

                    {mode === 'otp-request' && (
                        <form onSubmit={handleOtpRequest} className="login-form-inner">
                            <div className="login-field">
                                <label>Adresse email</label>
                                <div className="login-input-wrap">
                                    <FiMail className="login-input-icon" />
                                    <input
                                        type="email" value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder="votre@email.com" required autoFocus
                                    />
                                </div>
                            </div>
                            <button type="submit" className="login-submit" disabled={loading}>
                                {loading ? (
                                    <span className="login-spinner" />
                                ) : (
                                    <>Recevoir le code <FiArrowRight size={18} /></>
                                )}
                            </button>
                        </form>
                    )}

                    {mode === 'otp-verify' && (
                        <form onSubmit={handleOtpVerify} className="login-form-inner">
                            <div className="login-field">
                                <label>Code OTP</label>
                                <div className="login-input-wrap">
                                    <FiShield className="login-input-icon" />
                                    <input
                                        type="text" value={otp}
                                        onChange={e => setOtp(e.target.value)}
                                        placeholder="000000" maxLength={6}
                                        required autoFocus
                                        style={{ letterSpacing: 8, fontSize: 22, fontWeight: 700, textAlign: 'center' }}
                                    />
                                </div>
                            </div>
                            <button type="submit" className="login-submit" disabled={loading}>
                                {loading ? (
                                    <span className="login-spinner" />
                                ) : (
                                    <>Verifier <FiArrowRight size={18} /></>
                                )}
                            </button>
                            <button type="button" className="login-resend" onClick={() => setMode('otp-request')}>
                                Renvoyer le code
                            </button>
                        </form>
                    )}

                    <div className="login-card-footer">
                        <FiShield size={12} /> Connexion securisee
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
